import { useMemo, useState } from 'react';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { AsyncButton } from '@/components/common/AsyncButton';
import { swapFreeControlApi, fetchTradingGroups, fetchTradingLogins } from '@/api/services/swapFreeControl';
import type {
  GetConfigurationTargetsInput,
  SwapFreeConfigurationTargetDto,
} from '@/api/types/swapFreeControl';
import { AddTargetDialog } from './dialogs/AddTargetDialog';
import { EditExceptionsDialog } from './dialogs/EditExceptionsDialog';
import {
  applyOverlay,
  stageAddMany,
  stageExceptions,
  stageRemove,
  type StagingModel,
} from './staging';

interface Props {
  configId: string;
  isGroups: boolean;
  canEdit: boolean;
  staging: StagingModel | null; // non-null only when this config row is being edited
  onStagingChange: (next: StagingModel) => void;
  // Warn the user to press Edit first when they try to mutate without a staging session.
  onRequireEdit: () => void;
}

export function ConfigDetailTable({ configId, isGroups, canEdit, staging, onStagingChange, onRequireEdit }: Props) {
  const [addOpen, setAddOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<{ group: string; current: number[] } | null>(null);

  const extra = useMemo<GetConfigurationTargetsInput>(
    () => ({ configurationId: configId }),
    [configId],
  );
  const table = useServerDataTable<SwapFreeConfigurationTargetDto, GetConfigurationTargetsInput>({
    queryKey: ['swap-free', 'configuration-targets', configId],
    fetcher: (req) => swapFreeControlApi.getConfigurationTargets(req),
    pageSize: 5,
    extraParams: extra,
  });

  // Overlay staging (if any) onto the current server page.
  const overlay = useMemo(() => {
    if (!staging) return { rows: table.rows, totalCount: table.totalCount, stagedKeys: new Set<string>() };
    return applyOverlay(staging, table.rows, table.totalCount, table.pageIndex === 0);
  }, [staging, table.rows, table.totalCount, table.pageIndex]);

  const effectiveExceptions = (row: SwapFreeConfigurationTargetDto): number[] =>
    staging?.exceptions[row.target] ?? row.exceptions;

  const doAdd = (targets: string[]) => {
    if (!staging) return;
    onStagingChange(stageAddMany(staging, targets));
    setAddOpen(false);
    table.setPageIndex(0);
  };

  const doRemove = (row: SwapFreeConfigurationTargetDto) => {
    if (!staging) {
      onRequireEdit();
      return;
    }
    onStagingChange(stageRemove(staging, row.target));
  };

  const doSaveExceptions = (group: string, logins: number[]) => {
    if (!staging) return;
    onStagingChange(stageExceptions(staging, group, logins));
    setEditGroup(null);
  };

  const openEdit = (row: SwapFreeConfigurationTargetDto) => {
    if (!staging) {
      onRequireEdit();
      return;
    }
    setEditGroup({ group: row.target, current: effectiveExceptions(row) });
  };

  const columns: DataGridColumn<SwapFreeConfigurationTargetDto>[] = [
    {
      key: 'target',
      title: isGroups ? 'MT5 Group' : 'Login',
      className: 'text-start sfc-mono',
      value: (r) => r.target,
      cell: (r) => <span className="fw-semibold">{r.target}</span>,
    },
  ];

  if (isGroups) {
    columns.push({
      key: 'exceptions',
      title: 'Exceptions',
      sortable: false,
      filterable: false,
      className: 'text-start',
      value: (r) => effectiveExceptions(r).join(', '),
      cell: (r) => {
        const logins = effectiveExceptions(r);
        if (canEdit && staging) {
          return (
            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => openEdit(r)}>
              {logins.length ? (
                <span className="d-inline-flex flex-wrap gap-1">
                  {logins.map((l) => (
                    <span key={l} className="badge text-bg-light">{l}</span>
                  ))}
                </span>
              ) : (
                '—'
              )}
              <i className="fa fa-pencil ms-1" />
            </button>
          );
        }
        if (!logins.length) return '—';
        return (
          <span className="d-inline-flex flex-wrap gap-1">
            {logins.map((l) => (
              <span key={l} className="badge text-bg-light">{l}</span>
            ))}
          </span>
        );
      },
    });
  }

  if (canEdit) {
    columns.push({
      key: 'remove',
      title: '',
      sortable: false,
      hideable: false,
      filterable: false,
      className: 'text-center',
      value: () => '',
      cell: (r) => (
        <button type="button" className="btn btn-sm btn-link text-danger p-0" title="Remove" onClick={() => doRemove(r)}>
          <i className="fa fa-times" />
        </button>
      ),
    });
  }

  return (
    <div className="sfc-detail">
      <DataGrid
        columns={columns}
        data={overlay.rows}
        rowKey={(r) => r.target}
        columnVisibilityToggle={false}
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={table.setColumnFilter}
        rowClassName={(r) => (overlay.stagedKeys.has(r.target) ? 'sfc-staged-row' : undefined)}
        toolbarStart={
          canEdit ? (
            <AsyncButton
              className="btn btn-primary btn-sm"
              icon="fa fa-plus"
              onClick={() => {
                if (!staging) {
                  onRequireEdit();
                  return;
                }
                setAddOpen(true);
              }}
            >
              {isGroups ? 'Add Group' : 'Add Login'}
            </AsyncButton>
          ) : undefined
        }
        emptyMessage={table.isLoading ? 'Loading…' : 'No targets.'}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: Math.max(1, Math.ceil(overlay.totalCount / table.pageSize)),
          totalCount: overlay.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
      />

      <AddTargetDialog
        show={addOpen}
        isGroups={isGroups}
        fetch={isGroups ? fetchTradingGroups : fetchTradingLogins}
        onConfirm={doAdd}
        onClose={() => setAddOpen(false)}
      />

      <EditExceptionsDialog
        show={editGroup != null}
        group={editGroup?.group ?? null}
        current={editGroup?.current ?? []}
        onSave={doSaveExceptions}
        onClose={() => setEditGroup(null)}
      />
    </div>
  );
}
